<script setup>
/**
 * ClientGoalsInfoPage
 * Info page shown before agent catalog with relevant sales goals for the client.
 * Displays goal cards with matching products and agent progress.
 */
import { computed, ref, onMounted } from 'vue'
import { GOAL_TYPES } from '@core/composables/useGoals'
import {
  formatTimestamp,
  calculateAgentAmount,
  formatAgentAmount,
  isAgentMeetingGoal,
  getGoalTypeBadgeClass,
} from '@core/utils/goalHelpers'
import { callFunction } from '@core/firebase/functions.js'

const props = defineProps({
  goals: {
    type: Array,
    required: true,
    default: () => [],
  },
  client: {
    type: Object,
    required: true,
  },
  agentNumber: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['continue'])

const fallbackImage = ref('/images/placeholder-product.png')

function handleImageError(event) {
  event.target.src = fallbackImage.value
}

function getProductImage(product) {
  if (product.db_image?.img) {
    return product.db_image.img
  }
  return product.image || fallbackImage.value
}

function getGoalTypeLabel(type) {
  switch (type) {
    case GOAL_TYPES.QUANTITY:
      return 'כמותי'
    case GOAL_TYPES.DISTRIBUTION:
      return 'פיזור'
    case GOAL_TYPES.FINANCIAL:
      return 'כספי'
    default:
      return type
  }
}

function getAgentData(goal) {
  if (!props.agentNumber || !goal.agents) return null
  return goal.agents.find((a) => Number(a.id) === props.agentNumber)
}

function getAgentProgress(goal) {
  const agent = getAgentData(goal)
  if (!agent || !agent.goal) return null

  const achieved = calculateAgentAmount(agent, goal)
  const target = Number(agent.goal)
  const percent = target > 0 ? Math.min((achieved / target) * 100, 100) : 0
  const meeting = isAgentMeetingGoal(agent, goal)

  return {
    achieved: formatAgentAmount(agent, goal),
    target: target.toString(),
    percent: Math.round(percent),
    meeting,
  }
}

function getProgressUnit(goalType) {
  switch (goalType) {
    case GOAL_TYPES.QUANTITY:
      return "יח'"
    case GOAL_TYPES.DISTRIBUTION:
      return 'לקוחות'
    case GOAL_TYPES.FINANCIAL:
      return '\u20AA'
    default:
      return ''
  }
}

function isGoalCompleted(goal) {
  const progress = getAgentProgress(goal)
  if (!progress) return false
  return progress.meeting
}

function shouldShowCompletionStar(goal) {
  return isGoalCompleted(goal) && goal.type === GOAL_TYPES.QUANTITY
}

// Reorder goals: incomplete first, completed at the end
const orderedGoals = computed(() => {
  const incomplete = []
  const completed = []

  props.goals.forEach((goal) => {
    if (isGoalCompleted(goal)) {
      completed.push(goal)
    } else {
      incomplete.push(goal)
    }
  })

  return [...incomplete, ...completed]
})

// ── Monthly Items (unsold products per goal) ──
const monthlyItems = ref(null) // { [makat]: qty } or null while loading
const monthlyItemsLoading = ref(false)
const monthlyItemsError = ref(false)

onMounted(async () => {
  if (!props.client?.makat) return
  monthlyItemsLoading.value = true
  monthlyItemsError.value = false
  try {
    const result = await callFunction('getClientMonthlyItems', {
      clientId: props.client.makat,
    })
    monthlyItems.value = result?.items || {}
  } catch (_err) {
    monthlyItemsError.value = true
    monthlyItems.value = {}
  } finally {
    monthlyItemsLoading.value = false
  }
})

// ── Sold product popup ──
const soldPopup = ref(null) // { makat, name, qty }

function showSoldPopup(product) {
  if (!monthlyItems.value || !monthlyItems.value[product.makat]) return
  soldPopup.value = {
    makat: product.makat,
    name: product.name,
    qty: monthlyItems.value[product.makat],
  }
}

function closeSoldPopup() {
  soldPopup.value = null
}

function getUnsoldProducts(goal) {
  if (!monthlyItems.value || !goal._matchingProducts?.length) return []
  return goal._matchingProducts.filter((p) => !monthlyItems.value[p.makat])
}

function allProductsSold(goal) {
  if (!monthlyItems.value || !goal._matchingProducts?.length) return false
  return goal._matchingProducts.every((p) => monthlyItems.value[p.makat] > 0)
}

function handleContinue() {
  emit('continue')
}
</script>

<template>
  <div class="info-page">
    <!-- Header -->
    <div class="info-header">
      <div class="client-info">
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <h1 class="client-name">{{ client.name }}</h1>
        <span class="client-makat-badge">{{ client.makat }}</span>
      </div>
    </div>

    <!-- Goals Section -->
    <div class="goals-section">
      <!-- Goal Cards -->
      <div class="goals-grid">
        <div
          v-for="(goal, index) in orderedGoals"
          :key="goal.uid"
          class="goal-card"
          :class="{ 'goal-completed': isGoalCompleted(goal) }"
        >
          <!-- Goal Header -->
          <div class="goal-card-header">
            <h3 class="goal-title">{{ goal.title }}</h3>
            <!-- Completion Star (for completed quantity goals) -->
            <div v-if="shouldShowCompletionStar(goal)" class="goal-completion-star">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <polygon
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                />
              </svg>
            </div>
            <!-- Regular Type Badge -->
            <span v-else class="goal-type-badge" :class="getGoalTypeBadgeClass(goal.type)">
              {{ getGoalTypeLabel(goal.type) }}
            </span>
          </div>

          <!-- Date Range -->
          <div class="goal-dates">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>{{ formatTimestamp(goal.date_start_js) }}</span>
            <span class="date-arrow">&larr;</span>
            <span>{{ formatTimestamp(goal.date_end_js) }}</span>
          </div>

          <!-- Agent Progress -->
          <div v-if="getAgentProgress(goal)" class="agent-progress">
            <div class="progress-header">
              <span class="progress-label">התקדמות</span>
              <span class="progress-values">
                {{ getAgentProgress(goal).achieved }}
                <span class="progress-separator">/</span>
                {{ getAgentProgress(goal).target }}
                <span class="progress-unit">{{ getProgressUnit(goal.type) }}</span>
              </span>
            </div>
            <div class="progress-bar-track">
              <div
                class="progress-bar-fill"
                :class="{ meeting: getAgentProgress(goal).meeting }"
                :style="{ width: getAgentProgress(goal).percent + '%' }"
              ></div>
            </div>
            <span class="progress-percent" :class="{ meeting: getAgentProgress(goal).meeting }">
              {{ getAgentProgress(goal).percent }}%
            </span>
          </div>

          <!-- Matching Products Gallery -->
          <div v-if="goal._matchingProducts?.length" class="matching-products">
            <div class="products-label">מוצרים תואמים ({{ goal._matchingProducts.length }})</div>
            <div class="product-gallery">
              <div
                v-for="product in goal._matchingProducts"
                :key="product.makat"
                class="product-item"
                :class="{ 'product-sold': monthlyItems && monthlyItems[product.makat] > 0 }"
                @click="monthlyItems && monthlyItems[product.makat] > 0 && showSoldPopup(product)"
              >
                <img
                  :src="getProductImage(product)"
                  :alt="product.name"
                  @error="handleImageError"
                />
                <span class="product-makat">{{ product.makat }}</span>
              </div>
            </div>
          </div>

          <!-- Unsold Products column (visible on tablet/desktop only) -->
          <div class="goal-placeholder">
            <!-- Loading -->
            <div v-if="monthlyItemsLoading" class="placeholder-loading">
              <div class="placeholder-spinner"></div>
              <span>טוען נתוני מכירות...</span>
            </div>

            <!-- Error -->
            <div v-else-if="monthlyItemsError" class="placeholder-error">שגיאה בטעינת נתונים</div>

            <!-- All sold -->
            <div v-else-if="allProductsSold(goal)" class="placeholder-all-sold">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>כלל המוצרים נמכרו בחודש האחרון</span>
            </div>

            <!-- Unsold products list -->
            <div v-else class="placeholder-unsold">
              <div class="unsold-header">לא נמכרו ללקוח בחודש האחרון</div>
              <div class="unsold-list">
                <div
                  v-for="product in getUnsoldProducts(goal)"
                  :key="product.makat"
                  class="unsold-item"
                >
                  <span class="unsold-makat">{{ product.makat }}</span>
                  <span class="unsold-name">{{ product.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Continue Button -->
    <div class="continue-section">
      <button class="btn-continue" @click="handleContinue">
        <span>המשך לקטלוג הלקוח</span>
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    </div>

    <!-- Sold Product Popup -->
    <Teleport to="body">
      <div v-if="soldPopup" class="sold-popup-overlay" @click.self="closeSoldPopup">
        <div class="sold-popup">
          <div class="sold-popup-makat">{{ soldPopup.makat }}</div>
          <div class="sold-popup-name">{{ soldPopup.name }}</div>
          <div class="sold-popup-qty">
            נמכרו בחודש האחרון: <strong>{{ soldPopup.qty }}</strong> יח'
          </div>
          <button class="sold-popup-close" @click="closeSoldPopup">סגור</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.info-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 100vw;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--bg-secondary);
  animation: infoPageEnter 0.4s ease-out;
}

@keyframes infoPageEnter {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.info-header {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: var(--spacing-lg) var(--spacing-xl);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.client-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  min-width: 0;
}

.client-info svg {
  color: var(--color-success);
  flex-shrink: 0;
}

.client-name {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  min-width: 0;
  flex: 1;
}

.client-makat-badge {
  padding: 2px 10px;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

/* Goals Section */
.goals-section {
  flex: 1;
  padding: var(--spacing-lg) var(--spacing-xl);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  min-width: 0;
}

/* Goals Grid */
.goals-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-md);
  width: 100%;
}

/* Goal Card - 2-column grid on tablet/desktop */
.goal-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto auto auto;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  width: 100%;
  min-width: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.goal-card > * {
  min-width: 0;
}

.goal-card .goal-card-header {
  grid-column: 1 / -1;
}

.goal-card .goal-dates {
  grid-column: 1 / -1;
}

.goal-card .agent-progress {
  grid-column: 1;
  grid-row: 3;
}

.goal-card .matching-products {
  grid-column: 1;
  grid-row: 4;
  overflow-x: auto;
}

.goal-card .goal-placeholder {
  grid-column: 2;
  grid-row: 3 / -1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
}

/* Placeholder inner states */
.placeholder-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--text-muted);
  font-size: var(--font-size-xs);
}

.placeholder-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.placeholder-error {
  color: var(--color-error);
  font-size: var(--font-size-xs);
}

.placeholder-all-sold {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--color-success);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  padding: var(--spacing-sm);
  background: rgba(16, 185, 129, 0.08);
  border-radius: var(--border-radius);
  text-align: center;
}

.placeholder-unsold {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.unsold-header {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-error);
  padding-bottom: var(--spacing-xs);
  border-bottom: 1px solid var(--border-color);
}

.unsold-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.unsold-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 4px var(--spacing-xs);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
}

.unsold-makat {
  font-family: monospace;
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
  flex-shrink: 0;
}

.unsold-name {
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Completed Goal Card */
.goal-card.goal-completed {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 50%, #6ee7b7 100%);
  border-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  animation: goalCompletePulse 3s ease-in-out infinite;
}

@keyframes goalCompletePulse {
  0%,
  100% {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  }
  50% {
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }
}

.goal-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  min-width: 0;
}

.goal-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  min-width: 0;
  flex: 1;
}

/* Completed goal title - darker for readability on green background */
.goal-card.goal-completed .goal-title {
  color: #065f46;
}

.goal-type-badge {
  flex-shrink: 0;
  padding: 2px var(--spacing-sm);
  color: white;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-sm);
  background: var(--color-success);
}

.goal-type-badge.goal-type-quantity {
  background: #3b82f6;
}

.goal-type-badge.goal-type-distribution {
  background: #10b981;
}

.goal-type-badge.goal-type-financial {
  background: #f59e0b;
}

/* Completion Star (replaces badge on completed quantity goals) */
.goal-completion-star {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: #f59e0b;
  animation: starPulse 2s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3));
}

@keyframes starPulse {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 4px 8px rgba(245, 158, 11, 0.5));
  }
}

/* Dates */
.goal-dates {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.goal-dates svg {
  color: var(--text-muted);
}

.date-arrow {
  color: var(--text-muted);
  margin: 0 2px;
}

/* Completed goal dates - darker for readability */
.goal-card.goal-completed .goal-dates {
  color: #047857;
}

.goal-card.goal-completed .goal-dates svg,
.goal-card.goal-completed .date-arrow {
  color: #059669;
}

/* Agent Progress */
.agent-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
}

/* Enhanced agent progress section on completed goals */
.goal-card.goal-completed .agent-progress {
  background: rgba(255, 255, 255, 0.6);
}

.progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: var(--spacing-xs);
}

.progress-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.progress-values {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

/* Gold progress values on completed goals */
.goal-card.goal-completed .progress-values {
  color: #b45309;
  font-weight: var(--font-weight-bold);
}

.progress-separator {
  color: var(--text-muted);
  margin: 0 2px;
}

.progress-unit {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  font-weight: var(--font-weight-normal);
}

.progress-bar-track {
  width: 100%;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: var(--color-error);
  border-radius: 4px;
  transition: width 0.5s ease;
  min-width: 2px;
}

.progress-bar-fill.meeting {
  background: var(--color-success);
}

/* Enhanced progress bar on completed goal cards - gold gradient */
.goal-card.goal-completed .progress-bar-track {
  background: rgba(245, 158, 11, 0.15);
}

.goal-card.goal-completed .progress-bar-fill.meeting {
  background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%);
  box-shadow: 0 0 12px rgba(245, 158, 11, 0.5);
  animation: goldShimmer 2s ease-in-out infinite;
}

@keyframes goldShimmer {
  0%,
  100% {
    box-shadow: 0 0 12px rgba(245, 158, 11, 0.5);
  }
  50% {
    box-shadow: 0 0 16px rgba(245, 158, 11, 0.7);
  }
}

.progress-percent {
  font-size: var(--font-size-xs);
  color: var(--color-error);
  font-weight: var(--font-weight-semibold);
  text-align: left;
}

.progress-percent.meeting {
  color: var(--color-success);
}

/* Gold percentage text on completed goals */
.goal-card.goal-completed .progress-percent.meeting {
  color: #d97706;
  font-weight: var(--font-weight-bold);
}

/* Products */
.matching-products {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.products-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

/* Completed goal product label - darker for readability */
.goal-card.goal-completed .products-label,
.goal-card.goal-completed .progress-label {
  color: #047857;
}

.product-gallery {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  overflow-x: auto;
  min-height: 70px;
  width: 100%;
  max-width: 100%;
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
}

/* Enhanced product gallery on completed goals */
.goal-card.goal-completed .product-gallery {
  background: rgba(255, 255, 255, 0.6);
}

.product-gallery::-webkit-scrollbar {
  height: 4px;
}

.product-gallery::-webkit-scrollbar-track {
  background: var(--bg-tertiary);
  border-radius: 2px;
}

.product-gallery::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 2px;
}

.product-item {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.product-item img {
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: var(--border-radius-sm);
  background: var(--bg-primary);
  border: 2px solid transparent;
  transition: border-color 0.3s ease;
}

.product-item.product-sold img {
  border-color: var(--color-success);
}

.product-makat {
  font-size: 10px;
  color: var(--text-muted);
}

/* Continue Button */
.continue-section {
  position: sticky;
  bottom: 0;
  padding: var(--spacing-md) var(--spacing-xl);
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.btn-continue {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-md);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-continue:hover {
  background: var(--color-primary-dark);
}

.btn-continue:active {
  transform: scale(0.99);
}

/* Sold Product Popup */
.sold-popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.sold-popup {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  min-width: 240px;
  max-width: 320px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  animation: popupScale 0.2s ease-out;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

@keyframes popupScale {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.sold-popup-makat {
  font-family: monospace;
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

.sold-popup-name {
  font-size: var(--font-size-base);
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
}

.sold-popup-qty {
  font-size: var(--font-size-sm);
  color: var(--color-success);
  padding: var(--spacing-sm);
  background: rgba(16, 185, 129, 0.08);
  border-radius: var(--border-radius);
}

.sold-popup-close {
  padding: var(--spacing-xs) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  align-self: center;
}

.sold-popup-close:hover {
  background: var(--bg-tertiary);
}

/* Responsive */
@media (max-width: 767px) {
  .info-header {
    padding: var(--spacing-md);
  }

  .client-name {
    font-size: var(--font-size-lg);
  }

  .goals-section {
    padding: var(--spacing-md);
  }

  .goals-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }

  /* Stack vertically on mobile - single column */
  .goal-card {
    display: flex;
    flex-direction: column;
  }

  /* Hide placeholder on mobile */
  .goal-placeholder {
    display: none;
  }

  .continue-section {
    padding: var(--spacing-sm) var(--spacing-md);
  }
}

@media (min-width: 768px) and (max-width: 1279px) {
  .info-header {
    padding: var(--spacing-md) var(--spacing-lg);
  }

  .goals-section {
    padding: var(--spacing-md) var(--spacing-lg);
    max-width: 100%;
  }

  .continue-section {
    padding: var(--spacing-sm) var(--spacing-lg);
  }
}
</style>
